import { Drawer, Modal, theme } from 'antd'
import { FormSection } from 'components/form-section/form-section'
import { useTranslate, useWindowDimensions } from 'utils'
import { ScreenSize, ServiceLocation, ResourceGroup } from 'types'
import { NotFound, VerticalContainer } from 'components/styled'
import { PlusOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useField } from 'react-final-form'
import { useConfig } from 'api'
import { ServiceResourceConfigurator } from '../service-resource-configurator/service-resource-configurator'
import { ServiceLocationItem } from '../service-location-item/service-location-item'
import { ServiceAddLocation } from '../service-add-location/service-add-location'
import { ServiceChangeLocation } from '../service-change-location/service-change-location'

export const ServiceLocations = () => {

    //hooks:
    const { token: { padding, marginXXS, marginXS, margin, fontSize } } = theme.useToken()
    const { data: config } = useConfig()
    const { __ } = useTranslate()
    const { MD, LG, width, height } = useWindowDimensions()

    //fields:
    const { input: { value: lcs, onChange: updateLocations } } = useField('locations')
    const locations: ServiceLocation[] = lcs

    //state:
    const [ listLocations, setListLocation ] = useState<ServiceLocation[]>(lcs ?? [])
    const [ selectedLocation, setSelectedLocation ] = useState<ServiceLocation | null>(null)
    const [ isAddLocationOpen, setIsAddLocationOpen ] = useState(false)
    const [ isConfiguratorOpen, setIsConfiguratorOpen ] = useState(false)
    const [ isChangeLocationModalOpen, setIsLocationModalOpen ] = useState(false)
    const [ search, setSearch ] = useState<string | null>(null)

    const isConfiguratorModal = LG && height >= ScreenSize.MD

    useEffect(() => {
        if(search){
            const filteredLocations = [...lcs].filter(l => (
                l.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
                l.description?.toLocaleLowerCase().includes(search.toLocaleLowerCase())
            ))
            setListLocation(filteredLocations)
        }else{
            setListLocation([...lcs])
        }
    }, [locations, search])

    const getConfiguratorContent = () => (
        <div style={{
            height: isConfiguratorModal ? 700 : '100%',
            marginTop: (isConfiguratorModal && height < ScreenSize.LG) ? -80 : undefined
        }}>
            {selectedLocation && (
                <ServiceResourceConfigurator
                    selectedLocation={selectedLocation}
                    onSave={handleUpdateLocation}
                    onCancel={() => setIsConfiguratorOpen(false)}
                />
            )}
        </div>
    )

    const handleUpdateLocation = (resources: ResourceGroup[]) => {
        const locationIndex = locations.findIndex(l => l.id === selectedLocation?.id)
        const lcs = [...locations]
        lcs[locationIndex] = {
            ...lcs[locationIndex],
            resources: resources
        }
        updateLocations([...lcs])
        setIsConfiguratorOpen(false)
    }

    return config ? (

        <FormSection
            title={__`locations_and_resources`}
            topButton={((config.plan?.maxLocations ?? 1) >= 1) ? {
                label: __`add_location`,
                icon: <PlusOutlined/>,
                disabled: locations.length >= (config.plan?.maxLocations ?? 1),
                onClick: () => setIsAddLocationOpen(true)
            } : undefined}
            searchInput={{
                placeholder:__`search_locations`,
                onSearch: setSearch
            }}
            childrenWrapperStyle={{
                paddingRight: margin
            }}
        >
            
            {/* list */}
            {listLocations.length > 0 ? (
                <div
                    style={{
                        maxHeight: MD ? 500 : 350,
                        overflowY: 'auto'
                    }}
                >
                    <VerticalContainer
                        style={{
                            gap: marginXS,
                            paddingRight: MD ? marginXS : 0,
                            overflowX: 'hidden'
                        }}
                    >
                        {listLocations.map(location => (
                            <ServiceLocationItem
                                key={location.id}
                                location={location}
                                onSelect={() => {
                                    setSelectedLocation(location)
                                    setIsConfiguratorOpen(true)
                                }}
                                onRemove={() => {
                                    const newLocations = locations.filter(l => l.id !== location.id)
                                    updateLocations([...newLocations])
                                }}
                                onChangeLocation={() => {
                                    setSelectedLocation(location)
                                    setIsLocationModalOpen(true)
                                }}
                            />
                        ))}
                    </VerticalContainer>
                </div>
            ) : (
                <NotFound
                    message={search ? __`not_found` : __`this_service_is_not_provided_in_any_locations`}
                    style={{ fontSize, marginBottom: marginXXS }}
                />
            )}

            {/* add location */}
            <Modal
                open={isAddLocationOpen}
                closeIcon={null}
                footer={null}
                destroyOnClose
                style={{
                    minWidth: MD ? ScreenSize.MD : width,
                    height: 500,
                    padding
                }}
                styles={{ content: { padding: 0 } }}
                onCancel={() => setIsAddLocationOpen(false)}
            >

                <ServiceAddLocation
                    onAdd={addedLocations => {
                        updateLocations([...locations.concat(addedLocations)])
                        setIsAddLocationOpen(false)
                        if(addedLocations.length === 1){ // open the configurator if just only one location is added
                            setSelectedLocation(addedLocations[0])
                            setIsConfiguratorOpen(true)
                        }
                    }}
                    onClose={() => setIsAddLocationOpen(false)}
                />

            </Modal>

            <Modal
                open={isChangeLocationModalOpen}
                closeIcon={null}
                footer={null}
                destroyOnClose
                style={{
                    minWidth: MD ? ScreenSize.MD : width,
                    height: 500,
                    padding
                }}
                styles={{ content: { padding: 0 } }}
                onCancel={() => setIsLocationModalOpen(false)}
            >
                <ServiceChangeLocation
                    currentLocation={selectedLocation}
                    onChange={newLocation => {
                        const currentLocationIndex = locations.findIndex(location => location.id === selectedLocation?.id)
                        const clonedLocations = [...locations]
                        
                        newLocation && (clonedLocations[currentLocationIndex] = newLocation)
                        updateLocations(clonedLocations)
                        setIsLocationModalOpen(false)
                    }}
                    onClose={() => setIsLocationModalOpen(false)}
                />
            </Modal>
            
            {/* configurator */}
            {isConfiguratorModal ? (
                
                <Modal
                    open={isConfiguratorOpen}
                    closeIcon={null}
                    footer={null}
                    destroyOnClose
                    style={{
                        minWidth: LG ? ScreenSize.LG : width,
                        padding
                    }}
                    styles={{ content: { padding: 0 } }}
                    onCancel={() => setIsConfiguratorOpen(false)}
                >

                    {getConfiguratorContent()}

                </Modal>

            ) : (

                <Drawer
                    closable={false}
                    width={width}
                    destroyOnClose
                    open={isConfiguratorOpen}
                    styles={{ body: { padding: 0 }}}
                    onClose={() => setIsConfiguratorOpen(false)}
                >

                    {getConfiguratorContent()}
                    
                </Drawer>
                
            )}

        </FormSection>
    ) : <></>

}
